import { useCallback, useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { VitalDurationType } from '@fanduel/casino-datadog-logger/src/types';

import { useGeolocationHelpers } from '@/geolocation/geolocation-web/use-geolocation-helper';
import {
  PRECHECK_STATE,
  usePermissionPrecheck,
} from '@/geolocation/geolocation-web/use-permission-precheck';
import {
  setGameContext,
  setGeolocationContext,
  setGeolocationStatus,
  setPreChecksComplete,
} from '@/store/actions';
import { useStore } from '@/store/useStore';
import { PLATFORMS } from '@/utilities/constants/game';
import extractGeolocationToken from '@/utilities/helpers/extractGeolocationToken';
import { processGeolocationDurationVital } from '@/utilities/logger/datadog/vitals';
import getQueryParams from '@/utilities/getters/getQueryParams';

import { resolveLaunchGeoProduct } from './resolve-launch-geo-product';
import {
  registerGeolocationRelocate,
  unregisterGeolocationRelocate,
} from './request-geolocation-relocate';
import { Logger } from '@/utilities/logger/datadog/rumEvents/rumEvents';
import { hashValue } from '@/utilities/helpers/hashValue';
import { DdEventCategory, DdEventType } from '@/utilities/logger/datadog/types';

export const useGeolocation = () => {
  const hasActiveSession = useStore(
    (s) => s.userSessionContext.hasActiveSession,
  );
  const { platform, region } = useStore(
    useShallow((s) => ({
      platform: s.runtime.platform,
      region: s.runtime.region,
    })),
  );
  const { isAndroid, isXsell } = useStore(
    useShallow((s) => ({
      isAndroid: s.runtime.wrapperType.isAndroid,
      isXsell: s.runtime.wrapperType.isXsell,
    })),
  );
  const isNativeWrapper = isAndroid || isXsell;

  // Native wrappers own permissions themselves — there is no browser popup to
  // coordinate with, so the browser precheck is skipped off desktop.
  const { state: precheckState } = usePermissionPrecheck({
    skip: platform !== PLATFORMS.Desktop || isNativeWrapper,
  });
  const preChecksComplete = precheckState === PRECHECK_STATE.COMPLETE;

  useEffect(() => {
    setPreChecksComplete(preChecksComplete);
  }, [preChecksComplete]);

  const { sessionId, userId, authToken } = useStore(
    useShallow((s) => ({
      sessionId: s.userSessionContext.sessionId,
      userId: s.userSessionContext.userId,
      authToken: s.userSessionContext.authToken?.jwt,
    })),
  );
  const product = useStore((s) => s.runtime.product) as 'casino' | 'poker';
  const { provider, launchGeoProduct } = useStore(
    useShallow((s) => ({
      provider: s.gameContext.provider,
      launchGeoProduct: s.gameContext.launchGeoProduct,
    })),
  );
  const resolvedLaunchGeoProduct =
    launchGeoProduct && launchGeoProduct !== 'UNKNOWN'
      ? launchGeoProduct
      : resolveLaunchGeoProduct({
          runtimeProduct: product,
          providerName: getQueryParams('providerName') ?? provider,
          isAndroid,
        });

  const {
    geolocationToken,
    geolocationInProgress,
    geolocationSuccess,
    geolocationResetRevision,
  } = useStore(
    useShallow((s) => ({
      geolocationToken: s.geolocationContext.geolocationToken,
      geolocationInProgress: s.geolocationStatus.geolocationInProgress,
      geolocationSuccess: s.geolocationStatus.geolocationSuccess,
      geolocationResetRevision: s.geolocationResetRevision,
    })),
  );

  const { locateUser } = useGeolocationHelpers();
  const lastLocateRequestKey = useRef<string | null>(null);

  const triggerGeolocation = useCallback(
    ({
      force,
      product: locateProduct,
    }: {
      force?: boolean;
      product: 'casino' | 'poker';
    }) => {
      const shouldForceLocatingUser = !geolocationInProgress || force === true;

      const requiredProduct =
        resolvedLaunchGeoProduct === 'UNKNOWN'
          ? undefined
          : resolvedLaunchGeoProduct;
      const cachedGeoToken = extractGeolocationToken(requiredProduct);
      if (cachedGeoToken) {
        setGeolocationContext({ geolocationToken: cachedGeoToken });
        // On Xsell the native host geo-verifies the player and writes the
        // XsellGeocomplyToken cookie *before* the game-window app mounts, so no
        // in-app SDK/bridge pass ever fires. extractGeolocationToken has just
        // re-validated that cookie (expiry/session/region/product), so mark
        // geolocation as succeeded — this lets Timer's preChecksComplete
        // (hasActiveSession && geolocationSuccess) resolve. Other platforms only
        // flag it as previously verified and still run the locate below.
        setGeolocationStatus({
          previouslyVerified: true,
          ...(isXsell ? { geolocationSuccess: true } : {}),
        });

        hashValue(cachedGeoToken).then((geolocationTokenHash) => {
          Logger.logRumAction({
            name: '[GW][GEOLOCATION] Preliminary geolocation success',
            useCase: DdEventCategory.Geolocation,
            eventType: DdEventType.Success,
            context: {
              geolocationTokenHash,
            },
          });
        });
      }
      // Enhanced validation: ensure all required session data is present and valid
      // This prevents geocomply calls with invalid tokens during async login/logout
      processGeolocationDurationVital(VitalDurationType.START);

      const shouldLocateUser =
        shouldForceLocatingUser &&
        authToken &&
        (!geolocationSuccess || force === true);

      if (!shouldLocateUser) {
        return;
      }
      // Type guard: ensure all required values are strings before calling locateUser
      if (!region || !sessionId || !authToken || !userId) {
        return;
      }

      locateUser(
        {
          region,
          sessionId,
          userAuthToken: authToken,
          userId: String(userId),
          geocomplyToken: geolocationToken ?? cachedGeoToken,
          product: locateProduct,
        },
        locateProduct,
      );
    },
    [
      sessionId,
      region,
      userId,
      geolocationToken,
      geolocationInProgress,
      geolocationSuccess,
      authToken,
      locateUser,
      resolvedLaunchGeoProduct,
      isXsell,
    ],
  );

  useEffect(() => {
    if (!hasActiveSession) {
      lastLocateRequestKey.current = null;
      return;
    }
    // Hold the SDK until the browser permission prechecks have resolved so Chrome
    // permission popups stay responsive (see use-permission-precheck).
    if (!isNativeWrapper && !preChecksComplete) return;

    const locateProduct =
      resolvedLaunchGeoProduct === 'UNKNOWN'
        ? product
        : (resolvedLaunchGeoProduct.toLowerCase() as 'casino' | 'poker');

    const locateRequestKey = `${sessionId ?? ''}|${geolocationResetRevision}|${locateProduct}`;
    if (lastLocateRequestKey.current === locateRequestKey) return;
    lastLocateRequestKey.current = locateRequestKey;

    if (
      resolvedLaunchGeoProduct !== 'UNKNOWN' &&
      launchGeoProduct !== resolvedLaunchGeoProduct
    ) {
      setGameContext({ launchGeoProduct: resolvedLaunchGeoProduct });
    }

    triggerGeolocation({ product: locateProduct });
  }, [
    triggerGeolocation,
    hasActiveSession,
    isNativeWrapper,
    preChecksComplete,
    geolocationResetRevision,
    product,
    launchGeoProduct,
    resolvedLaunchGeoProduct,
    sessionId,
  ]);

  useEffect(() => {
    registerGeolocationRelocate(({ force } = {}) => {
      const locateProduct =
        resolvedLaunchGeoProduct === 'UNKNOWN'
          ? product
          : (resolvedLaunchGeoProduct.toLowerCase() as 'casino' | 'poker');

      triggerGeolocation({
        product: locateProduct,
        force: force ?? true,
      });
    });

    return unregisterGeolocationRelocate;
  }, [triggerGeolocation, product, resolvedLaunchGeoProduct]);
};
